import React, { useMemo, useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { garagesApi, spotsApi, uploadUrl, type SpotFeedPageApi } from '../../api/client';
import { spotApiToSpot } from '../../context/DataContext';
import type { GarageCarApi } from '../../api/client';
import type { Spot } from '../../types';
import type { GarageCarNavState } from '../../navigation/garageCarNav';
import styles from './UserProfile.module.css';

const SPOTS_PER_PAGE = 8;

function garageCardPhotoSrc(car: GarageCarApi): string | undefined {
  const u = car.photos?.[0] ?? car.images?.[0]?.url;
  if (!u) return undefined;
  return u.startsWith('http') ? u : uploadUrl(u);
}

export function UserProfile() {
  const { userId } = useParams<{ userId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getAuthor } = useAuth();
  const [profileGarage, setProfileGarage] = useState<GarageCarApi[]>([]);
  const [garageLoading, setGarageLoading] = useState(false);
  const [userSpots, setUserSpots] = useState<Spot[]>([]);
  const [spotsLoading, setSpotsLoading] = useState(false);

  const user = userId ? getAuthor(userId) : undefined;
  const userSpotsSorted = useMemo(() => {
    return [...userSpots].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [userSpots]);

  useEffect(() => {
    if (!userId?.trim()) return;
    let cancelled = false;
    setGarageLoading(true);
    garagesApi
      .getByUser(userId)
      .then((list) => {
        if (!cancelled) setProfileGarage(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setProfileGarage([]);
      })
      .finally(() => {
        if (!cancelled) setGarageLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  useEffect(() => {
    if (!userId?.trim()) return;
    let cancelled = false;

    const loadUserSpots = async () => {
      setSpotsLoading(true);
      try {
        const allSpots: Spot[] = [];
        let cursor: string | null | undefined = null;

        do {
          const page: SpotFeedPageApi = await spotsApi.feed({ authorId: userId, limit: 50, cursor: cursor ?? undefined });
          allSpots.push(...page.items.map(spotApiToSpot));
          cursor = page.nextCursor;
          if (!page.hasMore) break;
        } while (cursor);

        if (!cancelled) {
          setUserSpots(allSpots);
        }
      } catch {
        if (!cancelled) {
          setUserSpots([]);
        }
      } finally {
        if (!cancelled) {
          setSpotsLoading(false);
        }
      }
    };

    void loadUserSpots();

    return () => {
      cancelled = true;
    };
  }, [userId]);

  const currentGarageCars = useMemo(() => profileGarage.filter((c) => !c.isPastCar), [profileGarage]);
  const pastGarageCars = useMemo(() => profileGarage.filter((c) => c.isPastCar), [profileGarage]);

  const garageLinkState: GarageCarNavState | undefined = userId
    ? { garageBack: `/user/${userId}` }
    : undefined;

  const page = Math.max(1, parseInt(searchParams.get('page') ?? '1', 10) || 1);
  const totalPages = Math.max(1, Math.ceil(userSpotsSorted.length / SPOTS_PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * SPOTS_PER_PAGE;
  const spotsOnPage = userSpotsSorted.slice(pageStart, pageStart + SPOTS_PER_PAGE);

  const setPage = (p: number) => {
    const next = Math.max(1, Math.min(p, totalPages));
    setSearchParams(next === 1 ? {} : { page: String(next) });
  };

  if (!user) {
    return (
      <div className={styles.page}>
        <p>User not found.</p>
        <Link to="/">Back to feed</Link>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.profile}>
        <div className={styles.avatar}>
          {user.avatarUrl ? (
            <img
              src={user.avatarUrl.startsWith('http') ? user.avatarUrl : uploadUrl(user.avatarUrl)}
              alt=""
              className={styles.avatarImg}
            />
          ) : (
            <span className={styles.avatarLetter}>{user.displayName.charAt(0) || '?'}</span>
          )}
        </div>
        <h1>{user.displayName}</h1>
        <p className={styles.username}>@{user.username}</p>
        {user.bio && <p className={styles.bio}>{user.bio}</p>}
        {user.location && <p className={styles.location}>📍 {user.location}</p>}
      </div>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2>Garage{profileGarage.length > 0 ? ` (${profileGarage.length})` : ''}</h2>
          {profileGarage.length > 0 && userId && (
            <Link to={`/garages/user/${userId}`} className={styles.sectionLink}>
              Full garage →
            </Link>
          )}
        </div>
        {garageLoading && <p className={styles.muted}>Loading garage…</p>}
        {!garageLoading && profileGarage.length === 0 && <p className={styles.empty}>No cars in garage yet.</p>}
        {!garageLoading && currentGarageCars.length > 0 && (
          <>
            <h3 className={styles.subheading}>Current</h3>
            <div className={styles.garageGrid}>
              {currentGarageCars.map((car) => {
                const src = garageCardPhotoSrc(car);
                return (
                  <Link key={car.id} to={`/garage/${car.id}`} state={garageLinkState} className={styles.garageCard}>
                    <div className={styles.garageCardImage}>
                      {src ? <img src={src} alt="" /> : <span className={styles.placeholder}>🚗</span>}
                      {car.isForSale && <span className={styles.forSaleBanner}>For Sale</span>}
                    </div>
                    <span>
                      {[car.car?.year, car.car?.make, car.car?.model].filter(Boolean).join(' ')}
                    </span>
                  </Link>
                );
              })}
            </div>
          </>
        )}
        {!garageLoading && pastGarageCars.length > 0 && (
          <>
            <h3 className={styles.subheading}>Past cars</h3>
            <div className={styles.garageGrid}>
              {pastGarageCars.map((car) => {
                const src = garageCardPhotoSrc(car);
                return (
                  <Link key={car.id} to={`/garage/${car.id}`} state={garageLinkState} className={styles.garageCard}>
                    <div className={styles.garageCardImage}>
                      {src ? <img src={src} alt="" /> : <span className={styles.placeholder}>🚗</span>}
                      {car.isForSale && <span className={styles.forSaleBanner}>For Sale</span>}
                    </div>
                    <span>
                      {[car.car?.year, car.car?.make, car.car?.model].filter(Boolean).join(' ')}
                    </span>
                  </Link>
                );
              })}
            </div>
          </>
        )}
      </section>

      <section className={styles.section}>
        <h2>Spots ({userSpotsSorted.length})</h2>
        {spotsLoading ? (
          <p className={styles.muted}>Loading spots…</p>
        ) : userSpotsSorted.length === 0 ? (
          <p className={styles.empty}>No spots yet.</p>
        ) : (
          <>
            <div className={styles.spotGrid}>
              {spotsOnPage.map((spot) => (
                <Link key={spot.id} to={`/spot/${spot.id}`} className={styles.spotThumb}>
                  <img src={spot.imageUrl} alt="" loading="lazy" decoding="async" />
                  <span>
                    {spot.car.make} {spot.car.model}
                  </span>
                </Link>
              ))}
            </div>
            {totalPages > 1 && (
              <nav className={styles.pagination} aria-label="Spots pages">
                <button
                  type="button"
                  className={styles.pageLink}
                  disabled={currentPage <= 1}
                  onClick={() => setPage(currentPage - 1)}
                >
                  Previous
                </button>
                <span className={styles.pageInfo}>
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  type="button"
                  className={styles.pageLink}
                  disabled={currentPage >= totalPages}
                  onClick={() => setPage(currentPage + 1)}
                >
                  Next
                </button>
              </nav>
            )}
          </>
        )}
      </section>
    </div>
  );
}
